import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  collection, onSnapshot, query, where, doc,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  Download, FileSpreadsheet, Calendar, Filter, Users, MapPin,
  ChevronDown, Clock, Building2, FileText, CheckCircle2,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import LoadingSpinner from './components/LoadingSpinner';
import { generatePayrollBreakdown } from './utils/payRules';
import { getBlockWorkingHours, SEGMENT_TYPES } from './utils/shiftTypes';

// ─── Payroll format definitions ──────────────────────────────────────────────
const PAYROLL_FORMATS = [
  { id: 'excel',      label: 'Excel (.xlsx)',         icon: '📊', category: 'generic' },
  { id: 'csv',        label: 'CSV (.csv)',            icon: '📄', category: 'generic' },
  { id: 'xero',       label: 'Xero',                 icon: '🟦', category: 'payroll' },
  { id: 'quickbooks', label: 'QuickBooks',            icon: '🟩', category: 'payroll' },
  { id: 'sage',       label: 'Sage',                  icon: '🟢', category: 'payroll' },
  { id: 'adp',        label: 'ADP',                   icon: '🔴', category: 'payroll' },
  { id: 'gusto',      label: 'Gusto',                 icon: '🟠', category: 'payroll' },
  { id: 'paychex',    label: 'Paychex',               icon: '🔵', category: 'payroll' },
  { id: 'brightpay',  label: 'BrightPay',             icon: '🟡', category: 'payroll' },
  { id: 'deputy',     label: 'Deputy',                icon: '🟣', category: 'payroll' },
  { id: 'deel',       label: 'Deel',                  icon: '⚫', category: 'payroll' },
  { id: 'rippling',   label: 'Rippling',              icon: '🟤', category: 'payroll' },
];

// ─── Date helpers ────────────────────────────────────────────────────────────
function toDateStr(d) { return d.toISOString().split('T')[0]; }

function getPresetRange(preset) {
  const now = new Date();
  const start = new Date(now);
  switch (preset) {
    case 'today':
      return { start: toDateStr(now), end: toDateStr(now) };
    case 'thisWeek': {
      const day = now.getDay();
      const diff = day === 0 ? 6 : day - 1; // Monday start
      start.setDate(now.getDate() - diff);
      return { start: toDateStr(start), end: toDateStr(now) };
    }
    case 'lastWeek': {
      const day = now.getDay();
      const diff = day === 0 ? 6 : day - 1;
      const monday = new Date(now);
      monday.setDate(now.getDate() - diff - 7);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return { start: toDateStr(monday), end: toDateStr(sunday) };
    }
    case 'thisMonth':
      start.setDate(1);
      return { start: toDateStr(start), end: toDateStr(now) };
    case 'lastMonth': {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return { start: toDateStr(first), end: toDateStr(last) };
    }
    case 'custom':
    default:
      return null;
  }
}

function parseDate(dateStr) {
  return new Date(`${dateStr}T12:00:00`);
}

function formatLocalDateStr(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function addDays(dateStr, amount) {
  const date = parseDate(dateStr);
  date.setDate(date.getDate() + amount);
  return formatLocalDateStr(date);
}

function getWeekStartDate(dateStr, weekStartDay = 1) {
  const date = parseDate(dateStr);
  const day = date.getDay();
  const diff = (day - weekStartDay + 7) % 7;
  date.setDate(date.getDate() - diff);
  return formatLocalDateStr(date);
}

function getMonthStartDate(dateStr) {
  const date = parseDate(dateStr);
  date.setDate(1);
  return formatLocalDateStr(date);
}

// ─── Format time helpers ─────────────────────────────────────────────────────
function minsToHHMM(mins) {
  if (!mins && mins !== 0) return '';
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function minsToDecimal(mins) {
  if (!mins && mins !== 0) return 0;
  return Math.round((mins / 60) * 100) / 100;
}

function formatTime(isoStr) {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function getExportEmployeeId(record) {
  return record.payrollId || record.userId || '';
}

function getLegacyShiftMinutes(shift) {
  const [startHour = 0, startMinute = 0] = String(shift.startTime || '00:00').split(':').map(Number);
  const [endHour = 0, endMinute = 0] = String(shift.endTime || '00:00').split(':').map(Number);
  const start = (startHour * 60) + startMinute;
  let end = (endHour * 60) + endMinute;
  if (end <= start) end += 24 * 60;
  return end - start;
}

function getShiftWorkMetrics(shift) {
  if (shift.segments?.length > 0) {
    return {
      workingMinutesExcludingSleepIns: Math.round((getBlockWorkingHours(shift.segments, false) || 0) * 60),
      workingMinutesIncludingSleepIns: Math.round((getBlockWorkingHours(shift.segments, true) || 0) * 60),
      sleepInCount: shift.segments.filter((segment) => segment.type === SEGMENT_TYPES.SLEEP_IN).length,
    };
  }

  const legacyMinutes = getLegacyShiftMinutes(shift);
  return {
    workingMinutesExcludingSleepIns: legacyMinutes,
    workingMinutesIncludingSleepIns: legacyMinutes,
    sleepInCount: shift.hasSleepIn ? 1 : 0,
  };
}

function getPublishedPeriodStart(dateStr, aggregationPeriod, weekStartDay, anchorWeekStart) {
  if (aggregationPeriod === 'month') return getMonthStartDate(dateStr);

  const weekStart = getWeekStartDate(dateStr, weekStartDay);
  if (aggregationPeriod === 'week') return weekStart;

  const anchor = parseDate(anchorWeekStart || weekStart);
  const current = parseDate(weekStart);
  const diffDays = Math.floor((current.getTime() - anchor.getTime()) / (1000 * 60 * 60 * 24));
  const bucketIndex = Math.floor(diffDays / 14);
  const bucketStart = new Date(anchor);
  bucketStart.setDate(anchor.getDate() + (bucketIndex * 14));
  return formatLocalDateStr(bucketStart);
}

function getPublishedPeriodEnd(periodStart, aggregationPeriod) {
  if (aggregationPeriod === 'month') {
    const date = parseDate(periodStart);
    date.setMonth(date.getMonth() + 1, 0);
    return formatLocalDateStr(date);
  }
  return addDays(periodStart, aggregationPeriod === '2weeks' ? 13 : 6);
}

function buildPublishedPeriodRows(shifts, {
  aggregationPeriod,
  weekStartDay,
  anchorWeekStart,
  sleepInDisplayMode,
}) {
  const byPeriodStaff = {};

  shifts.forEach((shift) => {
    if (!shift.assignedStaffId) return;

    const periodStart = getPublishedPeriodStart(shift.date, aggregationPeriod, weekStartDay, anchorWeekStart);
    const periodEnd = getPublishedPeriodEnd(periodStart, aggregationPeriod);
    const key = `${shift.assignedStaffId}__${periodStart}`;
    const metrics = getShiftWorkMetrics(shift);

    if (!byPeriodStaff[key]) {
      byPeriodStaff[key] = {
        staffId: shift.assignedStaffId,
        staffName: shift.assignedStaffName || shift.staffName || '',
        payrollId: shift.payrollId || '',
        periodStart,
        periodEnd,
        publishedShiftCount: 0,
        workingMinutesExcludingSleepIns: 0,
        workingMinutesIncludingSleepIns: 0,
        sleepInCount: 0,
        locations: new Set(),
      };
    }

    const row = byPeriodStaff[key];
    row.staffName = row.staffName || shift.assignedStaffName || shift.staffName || '';
    row.payrollId = row.payrollId || shift.payrollId || '';
    row.publishedShiftCount += 1;
    row.workingMinutesExcludingSleepIns += metrics.workingMinutesExcludingSleepIns;
    row.workingMinutesIncludingSleepIns += metrics.workingMinutesIncludingSleepIns;
    row.sleepInCount += metrics.sleepInCount;
    if (shift.groupName) row.locations.add(shift.groupName);
  });

  return Object.values(byPeriodStaff)
    .sort((a, b) => {
      if (a.periodStart !== b.periodStart) return a.periodStart.localeCompare(b.periodStart);
      return String(a.staffName || '').localeCompare(String(b.staffName || ''));
    })
    .map((row) => {
      const exportMinutes = sleepInDisplayMode === 'hours'
        ? row.workingMinutesIncludingSleepIns
        : row.workingMinutesExcludingSleepIns;

      return {
        id: `${row.staffId}-${row.periodStart}`,
        userId: row.staffId,
        staffName: row.staffName,
        payrollId: row.payrollId,
        periodStart: row.periodStart,
        periodEnd: row.periodEnd,
        date: row.periodStart,
        groupName: row.locations.size === 0 ? '' : row.locations.size === 1 ? [...row.locations][0] : 'Multiple',
        publishedShiftCount: row.publishedShiftCount,
        workingMinutesExcludingSleepIns: row.workingMinutesExcludingSleepIns,
        workingMinutesIncludingSleepIns: row.workingMinutesIncludingSleepIns,
        sleepInCount: row.sleepInCount,
        exportMinutes,
      };
    });
}

function buildPublishedSummaryRows(periodRows) {
  const byStaff = {};

  periodRows.forEach((row) => {
    if (!byStaff[row.userId]) {
      byStaff[row.userId] = {
        staffName: row.staffName,
        payrollId: row.payrollId,
        periods: 0,
        shifts: 0,
        workingMinutesExcludingSleepIns: 0,
        workingMinutesIncludingSleepIns: 0,
        sleepInCount: 0,
        exportMinutes: 0,
      };
    }

    const summary = byStaff[row.userId];
    summary.periods += 1;
    summary.shifts += row.publishedShiftCount;
    summary.workingMinutesExcludingSleepIns += row.workingMinutesExcludingSleepIns;
    summary.workingMinutesIncludingSleepIns += row.workingMinutesIncludingSleepIns;
    summary.sleepInCount += row.sleepInCount;
    summary.exportMinutes += row.exportMinutes;
  });

  return Object.entries(byStaff).map(([id, row]) => ({
    'Employee Name': row.staffName,
    'Employee ID': row.payrollId || id,
    'Periods': row.periods,
    'Published Shifts': row.shifts,
    'Working Hours Excluding Sleep-ins': minsToDecimal(row.workingMinutesExcludingSleepIns),
    'Working Hours Including Sleep-ins': minsToDecimal(row.workingMinutesIncludingSleepIns),
    'Sleep-ins': row.sleepInCount,
    'Export Hours': minsToDecimal(row.exportMinutes),
  }));
}

function buildPublishedExportRows(periodRows) {
  return periodRows.map((row) => ({
    'Employee Name': row.staffName,
    'Employee ID': getExportEmployeeId(row),
    'Period Start': formatDate(row.periodStart),
    'Period End': formatDate(row.periodEnd),
    'Locations': row.groupName || '',
    'Published Shifts': row.publishedShiftCount,
    'Working Hours (Excluding Sleep-ins)': minsToDecimal(row.workingMinutesExcludingSleepIns),
    'Working Hours (Including Sleep-ins)': minsToDecimal(row.workingMinutesIncludingSleepIns),
    'Sleep-ins': row.sleepInCount,
    'Export Hours': minsToDecimal(row.exportMinutes),
  }));
}

// ─── Export builders per format ──────────────────────────────────────────────
function buildGenericRows(records) {
  return records.map(r => ({
    'Employee Name': r.staffName || '',
    'Employee ID':   getExportEmployeeId(r),
    'Date':          formatDate(r.date),
    'Location':      r.groupName || '',
    'Clock In':      formatTime(r.clockIn),
    'Clock Out':     formatTime(r.clockOut),
    'Hours (HH:MM)': minsToHHMM(r.duration),
    'Hours (Decimal)': minsToDecimal(r.duration),
    'Status':        r.status || '',
  }));
}

function buildXeroRows(records) {
  // Xero Timesheet Import format
  return records.map(r => ({
    'Employee':       r.staffName || '',
    'Earnings Rate':  'Ordinary Hours',
    'Tracking':       r.groupName || '',
    'Date':           formatDate(r.date),
    'Hours':          minsToDecimal(r.duration),
  }));
}

function buildQuickBooksRows(records) {
  // QuickBooks Time CSV format
  return records.map(r => ({
    'Employee':      r.staffName || '',
    'Date':          formatDate(r.date),
    'Job/Location':  r.groupName || '',
    'Start Time':    formatTime(r.clockIn),
    'End Time':      formatTime(r.clockOut),
    'Duration':      minsToDecimal(r.duration),
    'Total Hours':   minsToDecimal(r.duration),
    'Billable':      'Yes',
    'Notes':         '',
  }));
}

function buildSageRows(records) {
  // Sage Payroll import
  return records.map(r => ({
    'Employee Name':  r.staffName || '',
    'Pay Element':    'Basic Pay',
    'Department':     r.groupName || '',
    'Date':           formatDate(r.date),
    'Hours':          minsToDecimal(r.duration),
    'Rate':           '',
    'Amount':         '',
  }));
}

function buildADPRows(records) {
  // ADP Workforce Now format
  return records.map(r => ({
    'Co Code':           '',
    'Batch ID':          '',
    'File #':            getExportEmployeeId(r),
    'Employee Name':     r.staffName || '',
    'Reg Hours':         minsToDecimal(r.duration),
    'O/T Hours':         0,
    'Date Worked':       formatDate(r.date),
    'Department':        r.groupName || '',
    'Clock In':          formatTime(r.clockIn),
    'Clock Out':         formatTime(r.clockOut),
  }));
}

function buildGustoRows(records) {
  // Gusto hours import
  return records.map(r => ({
    'Employee Name':     r.staffName || '',
    'Date':              formatDate(r.date),
    'Regular Hours':     minsToDecimal(r.duration),
    'Overtime Hours':    0,
    'Double Overtime Hours': 0,
    'PTO Hours':         0,
    'Holiday Hours':     0,
    'Location':          r.groupName || '',
  }));
}

function buildPaychexRows(records) {
  // Paychex Flex import
  return records.map(r => ({
    'Worker ID':       getExportEmployeeId(r),
    'Employee Name':   r.staffName || '',
    'Check Date':      formatDate(r.date),
    'Earnings Code':   'REG',
    'Hours':           minsToDecimal(r.duration),
    'Rate':            '',
    'Amount':          '',
    'Department':      r.groupName || '',
  }));
}

function buildBrightPayRows(records) {
  // BrightPay CSV import
  return records.map(r => ({
    'Surname':         (r.staffName || '').split(' ').slice(-1).join(''),
    'Forename':        (r.staffName || '').split(' ').slice(0, -1).join(' ') || r.staffName || '',
    'Date':            formatDate(r.date),
    'Hours Worked':    minsToDecimal(r.duration),
    'Department':      r.groupName || '',
    'Start Time':      formatTime(r.clockIn),
    'End Time':        formatTime(r.clockOut),
  }));
}

function buildDeputyRows(records) {
  // Deputy Timesheet Export format
  return records.map(r => ({
    'Employee':      r.staffName || '',
    'Date':          formatDate(r.date),
    'Start':         formatTime(r.clockIn),
    'End':           formatTime(r.clockOut),
    'Mealbreak':     '00:00',
    'Total Hours':   minsToDecimal(r.duration),
    'Cost':          '',
    'Area':          r.groupName || '',
    'Status':        r.status === 'completed' ? 'Approved' : 'Pending',
  }));
}

function buildDeelRows(records) {
  // Deel timesheet import
  return records.map(r => ({
    'Team Member Name': r.staffName || '',
    'Date':             formatDate(r.date),
    'Hours':            minsToDecimal(r.duration),
    'Description':      `${r.groupName || ''} — ${formatTime(r.clockIn)} to ${formatTime(r.clockOut)}`,
  }));
}

function buildRipplingRows(records) {
  // Rippling Time & Attendance format
  return records.map(r => ({
    'Employee Name':   r.staffName || '',
    'Employee ID':     getExportEmployeeId(r),
    'Work Date':       formatDate(r.date),
    'Clock In':        formatTime(r.clockIn),
    'Clock Out':       formatTime(r.clockOut),
    'Regular Hours':   minsToDecimal(r.duration),
    'Overtime Hours':  0,
    'Department':      r.groupName || '',
    'Location':        r.groupName || '',
  }));
}

const FORMAT_BUILDERS = {
  excel:      buildGenericRows,
  csv:        buildGenericRows,
  xero:       buildXeroRows,
  quickbooks: buildQuickBooksRows,
  sage:       buildSageRows,
  adp:        buildADPRows,
  gusto:      buildGustoRows,
  paychex:    buildPaychexRows,
  brightpay:  buildBrightPayRows,
  deputy:     buildDeputyRows,
  deel:       buildDeelRows,
  rippling:   buildRipplingRows,
};

// ─── Summary builder ─────────────────────────────────────────────────────────
function buildSummaryRows(records) {
  const byStaff = {};
  records.forEach(r => {
    if (!byStaff[r.userId]) {
      byStaff[r.userId] = {
        name: r.staffName,
        payrollId: getExportEmployeeId(r),
        totalMins: 0,
        days: new Set(),
        records: 0,
      };
    }
    byStaff[r.userId].totalMins += r.duration || 0;
    byStaff[r.userId].days.add(r.date);
    byStaff[r.userId].records += 1;
  });
  return Object.entries(byStaff).map(([id, s]) => ({
    'Employee Name':   s.name,
    'Employee ID':     s.payrollId || id,
    'Total Days':      s.days.size,
    'Total Records':   s.records,
    'Total Hours (HH:MM)': minsToHHMM(s.totalMins),
    'Total Hours (Decimal)': minsToDecimal(s.totalMins),
  }));
}

// ─── Enhanced export rows with pay type breakdown ────────────────────────────
function buildEnhancedGenericRows(records, payRules) {
  const enriched = payRules ? generatePayrollBreakdown(records, [], {}, payRules) : records;
  return enriched.map(r => ({
    'Employee Name':       r.staffName || '',
    'Employee ID':         getExportEmployeeId(r),
    'Date':                formatDate(r.date),
    'Location':            r.groupName || '',
    'Clock In':            formatTime(r.clockIn),
    'Clock Out':           formatTime(r.clockOut),
    'Hours (HH:MM)':       minsToHHMM(r.duration),
    'Hours (Decimal)':     minsToDecimal(r.duration),
    'Standard Hours':      r.payBreakdown?.standardHours?.toFixed(2) || minsToDecimal(r.duration),
    'Overtime Hours':      r.payBreakdown?.overtimeHours?.toFixed(2) || '0.00',
    'Night Premium Hours': r.payBreakdown?.nightPremiumHours?.toFixed(2) || '0.00',
    'Weekend Hours':       r.payBreakdown?.weekendHours?.toFixed(2) || '0.00',
    'Bank Holiday Hours':  r.payBreakdown?.bankHolidayHours?.toFixed(2) || '0.00',
    'Sleep-in Fee':        r.payBreakdown?.sleepInFee?.toFixed(2) || '0.00',
    'On-call Fee':         r.payBreakdown?.onCallFee?.toFixed(2) || '0.00',
    'Total Pay':           r.payBreakdown?.totalPay?.toFixed(2) || '',
    'Status':              r.status || '',
  }));
}

function buildEnhancedSummaryRows(records, payRules) {
  const enriched = payRules ? generatePayrollBreakdown(records, [], {}, payRules) : records;
  const byStaff = {};
  enriched.forEach(r => {
    if (!byStaff[r.userId]) {
      byStaff[r.userId] = {
        name: r.staffName, payrollId: getExportEmployeeId(r), totalMins: 0, days: new Set(), records: 0,
        standardHours: 0, overtimeHours: 0, nightPremiumHours: 0,
        weekendHours: 0, bankHolidayHours: 0, sleepInFees: 0,
        onCallFees: 0, totalPay: 0,
      };
    }
    const s = byStaff[r.userId];
    s.totalMins += r.duration || 0;
    s.days.add(r.date);
    s.records += 1;
    if (r.payBreakdown) {
      s.standardHours += r.payBreakdown.standardHours || 0;
      s.overtimeHours += r.payBreakdown.overtimeHours || 0;
      s.nightPremiumHours += r.payBreakdown.nightPremiumHours || 0;
      s.weekendHours += r.payBreakdown.weekendHours || 0;
      s.bankHolidayHours += r.payBreakdown.bankHolidayHours || 0;
      s.sleepInFees += r.payBreakdown.sleepInFee || 0;
      s.onCallFees += r.payBreakdown.onCallFee || 0;
      s.totalPay += r.payBreakdown.totalPay || 0;
    }
  });

  return Object.entries(byStaff).map(([id, s]) => ({
    'Employee Name':         s.name,
    'Employee ID':           s.payrollId || id,
    'Total Days':            s.days.size,
    'Total Records':         s.records,
    'Total Hours (HH:MM)':   minsToHHMM(s.totalMins),
    'Total Hours (Decimal)': minsToDecimal(s.totalMins),
    'Standard Hours':        s.standardHours.toFixed(2),
    'Overtime Hours':        s.overtimeHours.toFixed(2),
    'Night Premium Hours':   s.nightPremiumHours.toFixed(2),
    'Weekend Hours':         s.weekendHours.toFixed(2),
    'Bank Holiday Hours':    s.bankHolidayHours.toFixed(2),
    'Sleep-in Fees':         s.sleepInFees.toFixed(2),
    'On-call Fees':          s.onCallFees.toFixed(2),
    'Total Pay':             s.totalPay.toFixed(2),
  }));
}

// ═════════════════════════════════════════════════════════════════════════════
// ─── Main Component ─────────────────────────────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════════

export default function PayrollExport({ user, userData, companyId }) {
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [records, setRecords] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [staffMeta, setStaffMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [loadError, setLoadError] = useState('');

  // Filters
  const [datePreset, setDatePreset] = useState('thisMonth');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('all');
  const [selectedStaff, setSelectedStaff] = useState('all');
  const [sourceMode, setSourceMode] = useState('timeRecords');
  const [aggregationPeriod, setAggregationPeriod] = useState('week');
  const [selectedFormat, setSelectedFormat] = useState('excel');
  const [includeSummary, setIncludeSummary] = useState(true);
  const [exportDone, setExportDone] = useState(false);
  const [payRules, setPayRules] = useState(null);
  const [includePayBreakdown, setIncludePayBreakdown] = useState(true);
  const [weekStartDay, setWeekStartDay] = useState(1);
  const [sleepInDisplayMode, setSleepInDisplayMode] = useState('hours');

  // Initialise date range from preset
  useEffect(() => {
    const range = getPresetRange(datePreset);
    if (range) {
      setStartDate(range.start);
      setEndDate(range.end);
    }
  }, [datePreset]);

  // Load groups
  useEffect(() => {
    if (!companyId) return;
    const unsub = onSnapshot(
      collection(db, 'companies', companyId, 'shiftsGroups'),
      (snap) => setGroups(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      (err) => console.error('Error loading groups:', err),
    );
    return unsub;
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    const unsub = onSnapshot(
      collection(db, 'companies', companyId, 'users'),
      (snap) => {
        const next = {};
        snap.forEach((d) => {
          const data = d.data() || {};
          const entry = {
            name: data.displayName || data.fullName || data.name || data.username || data.email || d.id,
            payrollId: data.payrollId || '',
          };
          next[d.id] = entry;
          if (data.assignmentId) next[data.assignmentId] = entry;
          if (data.uid) next[data.uid] = entry;
          if (data.userId) next[data.userId] = entry;
          if (data.authUid) next[data.authUid] = entry;
          if (data.staffId) next[data.staffId] = entry;
        });
        setStaffMeta(next);
      },
      (err) => console.error('Error loading staff payroll ids:', err),
    );
    return unsub;
  }, [companyId]);

  // Load pay rules from company settings
  useEffect(() => {
    if (!companyId) return;
    const unsub = onSnapshot(
      doc(db, 'companies', companyId),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const shiftsSettings = data.shiftsSettings || {};
          if (shiftsSettings.payRules) {
            setPayRules(shiftsSettings.payRules);
            setSleepInDisplayMode(shiftsSettings.payRules.sleepInDisplayMode || 'hours');
          }
          setWeekStartDay(shiftsSettings.weekStartDay ?? shiftsSettings.scheduling?.weekStartDay ?? 1);
        }
      },
      (err) => console.error('Error loading pay rules:', err),
    );
    return unsub;
  }, [companyId]);

  // Load time records for date range
  useEffect(() => {
    if (!companyId || !startDate || !endDate || sourceMode !== 'timeRecords') return;
    setLoading(true);
    setLoadError('');

    const q = query(
      collection(db, 'companies', companyId, 'timeRecords'),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
    );

    const unsub = onSnapshot(q, (snap) => {
      const nextRecords = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter((record) => record.status === 'completed')
        .sort((a, b) => {
          if (a.date !== b.date) return String(a.date || '').localeCompare(String(b.date || ''));
          return String(a.clockIn || '').localeCompare(String(b.clockIn || ''));
        });
      setRecords(nextRecords);
      setLoading(false);
    }, (err) => {
      console.error('Error loading time records:', err);
      setLoadError(
        err?.code === 'permission-denied'
          ? t('payroll.permissionDenied', 'You do not have permission to view payroll records.')
          : t('payroll.loadFailed', 'Payroll data could not be loaded. Please check your filters or Firestore indexes.')
      );
      setLoading(false);
    });
    return unsub;
  }, [companyId, startDate, endDate, sourceMode, t]);

  useEffect(() => {
    if (!companyId || !startDate || !endDate || sourceMode !== 'publishedRotas') return;
    setLoading(true);
    setLoadError('');

    const q = query(
      collection(db, 'companies', companyId, 'shifts'),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
    );

    const unsub = onSnapshot(q, (snap) => {
      const nextShifts = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter((shift) => shift.status === 'published')
        .sort((a, b) => {
          if (a.date !== b.date) return String(a.date || '').localeCompare(String(b.date || ''));
          return String(a.startTime || '').localeCompare(String(b.startTime || ''));
        });
      setShifts(nextShifts);
      setLoading(false);
    }, (err) => {
      console.error('Error loading published shifts:', err);
      setLoadError(
        err?.code === 'permission-denied'
          ? t('payroll.permissionDenied', 'You do not have permission to view payroll records.')
          : t('payroll.loadFailed', 'Payroll data could not be loaded. Please check your filters or Firestore indexes.')
      );
      setLoading(false);
    });

    return unsub;
  }, [companyId, startDate, endDate, sourceMode, t]);

  const enrichedRecords = useMemo(() => records.map((r) => {
    const staffId = r.userId || r.uid || '';
    const meta = staffMeta[staffId] || {};
    return {
      ...r,
      userId: staffId,
      staffName: r.staffName || meta.name || staffId,
      payrollId: r.payrollId || meta.payrollId || '',
    };
  }), [records, staffMeta]);

  const enrichedShifts = useMemo(() => shifts.map((shift) => {
    const staffId = shift.assignedStaffId || shift.assignedTo || '';
    const meta = staffMeta[staffId] || {};
    const group = groups.find((item) => item.id === shift.groupId) || {};
    return {
      ...shift,
      assignedStaffId: staffId,
      userId: staffId,
      staffName: shift.assignedStaffName || shift.assignedToName || meta.name || staffId,
      assignedStaffName: shift.assignedStaffName || shift.assignedToName || meta.name || staffId,
      payrollId: shift.payrollId || meta.payrollId || '',
      groupName: shift.groupName || group.name || '',
      groupColor: shift.groupColor || group.color || '',
    };
  }), [groups, shifts, staffMeta]);

  // Derive unique staff from records
  const staffList = useMemo(() => {
    const map = {};
    Object.entries(staffMeta).forEach(([id, meta]) => {
      if (!id || !meta?.name) return;
      map[id] = {
        name: meta.name,
        payrollId: meta.payrollId || '',
      };
    });

    const sourceRecords = sourceMode === 'publishedRotas' ? enrichedShifts : enrichedRecords;
    sourceRecords.forEach((r) => {
      if (r.userId && !map[r.userId]) {
        map[r.userId] = {
          name: r.staffName || r.assignedStaffName || r.userId,
          payrollId: r.payrollId || '',
        };
      }
    });

    return Object.entries(map)
      .map(([id, meta]) => ({
        id,
        name: meta.payrollId ? `${meta.name} (${meta.payrollId})` : meta.name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [enrichedRecords, enrichedShifts, sourceMode, staffMeta]);

  // Apply group + staff filter
  const filteredRecords = useMemo(() => {
    const sourceRecords = sourceMode === 'publishedRotas' ? enrichedShifts : enrichedRecords;
    return sourceRecords.filter(r => {
      if (selectedGroupId !== 'all' && r.groupId !== selectedGroupId) return false;
      if (selectedStaff !== 'all' && r.userId !== selectedStaff) return false;
      return true;
    });
  }, [enrichedRecords, enrichedShifts, selectedGroupId, selectedStaff, sourceMode]);

  const publishedPeriodRows = useMemo(() => {
    if (sourceMode !== 'publishedRotas' || !startDate) return [];
    const anchorWeekStart = getWeekStartDate(startDate, weekStartDay);
    return buildPublishedPeriodRows(filteredRecords, {
      aggregationPeriod,
      weekStartDay,
      anchorWeekStart,
      sleepInDisplayMode,
    });
  }, [aggregationPeriod, filteredRecords, sleepInDisplayMode, sourceMode, startDate, weekStartDay]);

  const previewRows = sourceMode === 'publishedRotas' ? publishedPeriodRows : filteredRecords;

  // Stats
  const totalHours = useMemo(() => {
    if (sourceMode === 'publishedRotas') {
      return minsToDecimal(previewRows.reduce((sum, row) => sum + (row.exportMinutes || 0), 0));
    }
    return minsToDecimal(filteredRecords.reduce((sum, record) => sum + (record.duration || 0), 0));
  }, [filteredRecords, previewRows, sourceMode]);
  const uniqueStaff = useMemo(() => new Set(previewRows.map(r => r.userId)).size, [previewRows]);

  // ── Export handler ─────────────────────────────────────────────────────────
  const handleExport = () => {
    if (previewRows.length === 0) return;
    setExporting(true);
    setExportDone(false);

    try {
      const isPublishedExport = sourceMode === 'publishedRotas';
      const isGenericFormat = ['excel', 'csv'].includes(selectedFormat);
      const useEnhanced = !isPublishedExport && isGenericFormat && includePayBreakdown && payRules;
      const rows = isPublishedExport
        ? buildPublishedExportRows(previewRows)
        : (useEnhanced
          ? buildEnhancedGenericRows(filteredRecords, payRules)
          : (FORMAT_BUILDERS[selectedFormat] || buildGenericRows)(filteredRecords));
      const formatDef = PAYROLL_FORMATS.find(f => f.id === selectedFormat);
      const label = formatDef?.label || 'Export';
      const sourceLabel = isPublishedExport ? `PublishedRota-${aggregationPeriod}` : label.replace(/[^a-zA-Z0-9]/g, '');
      const filename = `BuildMyShifts-${sourceLabel}-${startDate}-to-${endDate}`;

      if (selectedFormat === 'csv') {
        // Plain CSV export
        const ws = XLSX.utils.json_to_sheet(rows);
        const csvContent = XLSX.utils.sheet_to_csv(ws);
        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8' });
        saveAs(blob, `${filename}.csv`);
      } else {
        // Excel workbook (all formats → .xlsx with appropriate column mapping)
        const wb = XLSX.utils.book_new();

        // Main timesheet data
        const ws = XLSX.utils.json_to_sheet(rows);
        // Auto-size columns
        const colWidths = Object.keys(rows[0] || {}).map(k => ({
          wch: Math.max(k.length, ...rows.map(r => String(r[k] || '').length)) + 2,
        }));
        ws['!cols'] = colWidths;
        XLSX.utils.book_append_sheet(wb, ws, isPublishedExport ? 'Published Hours' : 'Timesheet');

        // Summary sheet (with pay breakdown if enabled)
        if (includeSummary) {
          const summaryRows = isPublishedExport
            ? buildPublishedSummaryRows(previewRows)
            : (useEnhanced
              ? buildEnhancedSummaryRows(filteredRecords, payRules)
              : buildSummaryRows(filteredRecords));
          if (summaryRows.length > 0) {
            const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
            const sumColWidths = Object.keys(summaryRows[0]).map(k => ({
              wch: Math.max(k.length, ...summaryRows.map(r => String(r[k] || '').length)) + 2,
            }));
            wsSummary['!cols'] = sumColWidths;
            XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');
          }
        }

        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `${filename}.xlsx`);
      }

      setExportDone(true);
      setTimeout(() => setExportDone(false), 3000);
    } catch (err) {
      console.error('Export error:', err);
      alert(t('payroll.exportFailed'));
    }
    setExporting(false);
  };

  if (loading && records.length === 0 && shifts.length === 0) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <FileSpreadsheet size={24} className="text-primary" />
          {t('payroll.title')}
        </h1>
        <p className="text-gray-400 mt-1">{t('payroll.subtitle')}</p>
      </div>

      {loadError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-sm text-red-300">
          {loadError}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ── Left: Filters ─────────────────────────────────────────────── */}
        <div className="lg:col-span-1 space-y-4">
            {/* Date Range */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5 space-y-3">
            <h3 className="text-sm font-semibold text-white flex items-center gap-1.5">
              <Calendar size={16} className="text-primary" />
              {t('payroll.dateRange')}
            </h3>

            {/* Presets */}
            <div className="flex flex-wrap gap-1.5">
              {['today', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth', 'custom'].map(p => (
                <button
                  key={p}
                  onClick={() => setDatePreset(p)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    datePreset === p
                      ? 'bg-primary text-white'
                      : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {t(`payroll.preset_${p}`)}
                </button>
              ))}
            </div>

            {/* Date inputs */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('payroll.from')}</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={e => { setStartDate(e.target.value); setDatePreset('custom'); }}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-sm text-white"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('payroll.to')}</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={e => { setEndDate(e.target.value); setDatePreset('custom'); }}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-sm text-white"
                />
              </div>
            </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5 space-y-3">
              <h3 className="text-sm font-semibold text-white flex items-center gap-1.5">
                <FileText size={16} className="text-primary" />
                {t('payroll.exportSource', 'Export Source')}
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'timeRecords', label: t('payroll.sourceTimeRecords', 'Time Records') },
                  { id: 'publishedRotas', label: t('payroll.sourcePublishedRotas', 'Published Rotas') },
                ].map((option) => (
                  <button
                    key={option.id}
                    onClick={() => {
                      setSourceMode(option.id);
                      if (option.id === 'publishedRotas' && !['excel', 'csv'].includes(selectedFormat)) {
                        setSelectedFormat('excel');
                      }
                    }}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      sourceMode === option.id
                        ? 'bg-primary text-white'
                        : 'bg-white/5 text-gray-300 hover:bg-white/10'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              {sourceMode === 'publishedRotas' && (
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('payroll.aggregationPeriod', 'Aggregation')}</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { id: 'week', label: t('payroll.aggregateWeek', 'Week') },
                      { id: '2weeks', label: t('payroll.aggregate2Weeks', '2 Weeks') },
                      { id: 'month', label: t('payroll.aggregateMonth', 'Month') },
                    ].map((option) => (
                      <button
                        key={option.id}
                        onClick={() => setAggregationPeriod(option.id)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          aggregationPeriod === option.id
                            ? 'bg-primary text-white'
                            : 'bg-white/5 text-gray-300 hover:bg-white/10'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {sleepInDisplayMode === 'hours'
                      ? t('payroll.sleepInHoursMode', 'Sleep-ins are added into exported hours.')
                      : t('payroll.sleepInCountMode', 'Sleep-ins stay separate and are exported as a count.')}
                  </p>
                </div>
              )}
            </div>

          {/* Filters */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5 space-y-3">
            <h3 className="text-sm font-semibold text-white flex items-center gap-1.5">
              <Filter size={16} className="text-primary" />
              {t('payroll.filters')}
            </h3>

            {/* Location filter */}
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('payroll.location')}</label>
              <div className="relative">
                <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <select
                  value={selectedGroupId}
                  onChange={e => setSelectedGroupId(e.target.value)}
                  className="w-full pl-8 pr-8 py-2 bg-white/5 border border-white/20 rounded-lg text-sm text-white appearance-none cursor-pointer"
                >
                  <option value="all" className="bg-slate-800">{t('payroll.allLocations')}</option>
                  {groups.map(g => (
                    <option key={g.id} value={g.id} className="bg-slate-800">{g.name}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Staff filter */}
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('payroll.employee')}</label>
              <div className="relative">
                <Users size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <select
                  value={selectedStaff}
                  onChange={e => setSelectedStaff(e.target.value)}
                  className="w-full pl-8 pr-8 py-2 bg-white/5 border border-white/20 rounded-lg text-sm text-white appearance-none cursor-pointer"
                >
                  <option value="all" className="bg-slate-800">{t('payroll.allStaff')}</option>
                  {staffList.map(s => (
                    <option key={s.id} value={s.id} className="bg-slate-800">{s.name}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Include summary toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeSummary}
                onChange={e => setIncludeSummary(e.target.checked)}
                className="w-4 h-4 rounded border-white/20 bg-white/5 text-primary focus:ring-primary"
              />
              <span className="text-sm text-gray-300">{t('payroll.includeSummary')}</span>
            </label>

            {/* Pay breakdown toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includePayBreakdown}
                onChange={e => setIncludePayBreakdown(e.target.checked)}
                className="w-4 h-4 rounded border-white/20 bg-white/5 text-primary focus:ring-primary"
              />
              <span className="text-sm text-gray-300">
                {t('payroll.includePayBreakdown', 'Include pay type breakdown')}
                {!payRules && <span className="text-xs text-amber-400 ml-1">(configure pay rules in Settings)</span>}
              </span>
            </label>
          </div>

          {/* Quick stats */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-1.5">
              <Clock size={16} className="text-primary" />
              {t('payroll.overview')}
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center">
                <p className="text-xl font-bold text-white">{previewRows.length}</p>
                <p className="text-xs text-gray-400">
                  {sourceMode === 'publishedRotas'
                    ? t('payroll.periods', 'Periods')
                    : t('payroll.records')}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-primary">{totalHours}h</p>
                <p className="text-xs text-gray-400">{t('payroll.totalHours')}</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-white">{uniqueStaff}</p>
                <p className="text-xs text-gray-400">{t('payroll.staff')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Right: Format Selection + Export ──────────────────────────── */}
        <div className="lg:col-span-2 space-y-4">
          {/* Format picker */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-1.5">
              <FileText size={16} className="text-primary" />
              {t('payroll.exportFormat')}
            </h3>

            {/* Generic formats */}
            <p className="text-xs text-gray-500 uppercase font-medium mb-2">{t('payroll.genericFormats')}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
              {PAYROLL_FORMATS.filter(f => f.category === 'generic').map(f => (
                <button
                  key={f.id}
                  onClick={() => setSelectedFormat(f.id)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                    selectedFormat === f.id
                      ? 'bg-primary/20 border-primary/50 text-primary'
                      : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10 hover:border-white/20'
                  }`}
                >
                  <span className="text-lg">{f.icon}</span>
                  {f.label}
                </button>
              ))}
            </div>

            {/* Payroll-specific formats */}
            {sourceMode !== 'publishedRotas' && (
              <>
                <p className="text-xs text-gray-500 uppercase font-medium mb-2">{t('payroll.payrollFormats')}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {PAYROLL_FORMATS.filter(f => f.category === 'payroll').map(f => (
                    <button
                      key={f.id}
                      onClick={() => setSelectedFormat(f.id)}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                        selectedFormat === f.id
                          ? 'bg-primary/20 border-primary/50 text-primary'
                          : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10 hover:border-white/20'
                      }`}
                    >
                      <span className="text-lg">{f.icon}</span>
                      {f.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Data preview */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-1.5">
                <Building2 size={16} className="text-primary" />
                {t('payroll.preview')} ({previewRows.length} {sourceMode === 'publishedRotas' ? t('payroll.periods', 'Periods') : t('payroll.records')})
              </h3>

            {previewRows.length === 0 ? (
              <div className="text-center py-8">
                <FileSpreadsheet size={40} className="text-gray-600 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">{t('payroll.noRecords')}</p>
                <p className="text-gray-600 text-xs mt-1">{t('payroll.noRecordsHint')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto max-h-80 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 px-2 text-gray-400 font-medium">{t('payroll.col_employee')}</th>
                      <th className="text-left py-2 px-2 text-gray-400 font-medium">
                        {sourceMode === 'publishedRotas' ? t('payroll.col_period', 'Period') : t('payroll.col_date')}
                      </th>
                      <th className="text-left py-2 px-2 text-gray-400 font-medium">{t('payroll.col_location')}</th>
                      <th className="text-left py-2 px-2 text-gray-400 font-medium">
                        {sourceMode === 'publishedRotas' ? t('payroll.col_shifts', 'Shifts') : t('payroll.col_clockIn')}
                      </th>
                      <th className="text-left py-2 px-2 text-gray-400 font-medium">
                        {sourceMode === 'publishedRotas' ? t('payroll.col_sleepIns', 'Sleep-ins') : t('payroll.col_clockOut')}
                      </th>
                      <th className="text-right py-2 px-2 text-gray-400 font-medium">{t('payroll.col_hours')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.slice(0, 50).map(r => (
                      <tr key={r.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="py-1.5 px-2 text-white">{r.staffName}</td>
                        <td className="py-1.5 px-2 text-gray-300">
                          {sourceMode === 'publishedRotas'
                            ? `${formatDate(r.periodStart)} - ${formatDate(r.periodEnd)}`
                            : formatDate(r.date)}
                        </td>
                        <td className="py-1.5 px-2">
                          <span className="inline-flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: r.groupColor || '#2563EB' }} />
                            <span className="text-gray-300">{r.groupName}</span>
                          </span>
                        </td>
                        <td className="py-1.5 px-2 text-gray-300 font-mono">
                          {sourceMode === 'publishedRotas' ? r.publishedShiftCount : formatTime(r.clockIn)}
                        </td>
                        <td className="py-1.5 px-2 text-gray-300 font-mono">
                          {sourceMode === 'publishedRotas' ? r.sleepInCount : formatTime(r.clockOut)}
                        </td>
                        <td className="py-1.5 px-2 text-right text-primary font-medium">
                          {sourceMode === 'publishedRotas' ? minsToDecimal(r.exportMinutes) : minsToDecimal(r.duration)}h
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {previewRows.length > 50 && (
                  <p className="text-center text-xs text-gray-500 py-2">
                    {t('payroll.showingFirst', { count: 50, total: previewRows.length })}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Export button */}
          <button
            onClick={handleExport}
            disabled={exporting || previewRows.length === 0}
            className={`w-full flex items-center justify-center gap-2 py-4 rounded-xl text-lg font-bold transition-all ${
              exportDone
                ? 'bg-green-600 text-white'
                : previewRows.length === 0
                  ? 'bg-white/5 text-gray-500 cursor-not-allowed'
                  : 'bg-primary hover:bg-primary-dark text-white shadow-lg shadow-primary/20 hover:scale-[1.01] active:scale-[0.99]'
            }`}
          >
            {exportDone ? (
              <>
                <CheckCircle2 size={22} />
                {t('payroll.exported')}
              </>
            ) : exporting ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('payroll.exporting')}
              </>
            ) : (
              <>
                <Download size={22} />
                {t('payroll.exportButton', { format: PAYROLL_FORMATS.find(f => f.id === selectedFormat)?.label || '' })}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
